// ═══════════════════════════════════════════════════════════════════════════════
// TemplateReactionsModal.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Per-template reaction assignment modal. Opens from ManageTemplates when an
// admin clicks the Reactions button on a template row. Lets the admin choose
// which emojis from the library appear on cards using this template, set the
// order they appear in, optionally disable reactions on this template
// entirely, or fall back to the library defaults.
//
// USAGE
//   <TemplateReactionsModal
//     template={template}       // { templateid, name, ... }
//     onClose={() => ...}
//     onSaved={() => ...}       // called after a successful save
//   />
//
// LOAD SHAPE (adminService.getTemplateReactions(templateId)):
//   {
//     reactions_enabled: true,
//     assigned: [{ emojiid, emoji_char, display_name, display_order }, ...],
//     library:  [{ emojiid, emoji_char, display_name, isactive, isdefault }, ...],
//   }
//
// SAVE PAYLOAD (adminService.setTemplateReactions(templateId, payload)):
//   {
//     reactions_enabled: true,
//     emojiids: [emojiid, emojiid, ...],   // order matters
//   }
//
// BEHAVIOR
//   • Loads on mount, shows the assigned emojis in their saved order and the
//     remaining library emojis below.
//   • Toggle to disable reactions on this template — when off, the picker is
//     dimmed and disabled.
//   • "Use defaults" link resets the selection to the library's current
//     default set (emojis where isdefault === true).
//   • Drag-to-reorder the selected list. Reordering is local only until Save.
//   • Live preview at the bottom shows what the floating reaction bar will
//     look like on the actual card.
//   • Inactive library emojis are still shown if currently assigned to the
//     template (they keep working), but greyed out and unselectable for new
//     additions — matches the "keep on existing templates" deactivate path.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import adminService from '../../services/adminService.js';

// ─── Selected Emoji Row (draggable) ───────────────────────────────────────
function SelectedRow({
  emoji,
  index,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onRemove,
  disabled,
}) {
  const isInactive = emoji.isactive === false;

  return (
    <div
      draggable={!disabled}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, index)}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
        isDragging
          ? 'border-gold bg-gold/10 cursor-grabbing'
          : 'border-gold/40 bg-gold/5'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`text-royal-200 select-none px-1 ${disabled ? '' : 'cursor-grab'}`}
        aria-label="Drag to reorder"
        title="Drag to reorder"
      >
        ⋮⋮
      </span>
      <span className="text-2xl leading-none" aria-hidden="true">
        {emoji.emoji_char}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-medium">{emoji.display_name}</span>
          {isInactive && (
            <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 text-xs rounded-full">
              Library disabled
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onRemove(emoji.emojiid)}
        disabled={disabled}
        className="text-red-400 hover:text-red-300 text-sm transition-colors disabled:opacity-50"
        aria-label={`Remove ${emoji.display_name}`}
      >
        Remove
      </button>
    </div>
  );
}

// ─── Library Pick Row (not currently selected) ────────────────────────────
function LibraryPickRow({ emoji, onAdd, disabled }) {
  const isInactive = emoji.isactive === false;
  const blocked = disabled || isInactive;

  return (
    <button
      onClick={() => !blocked && onAdd(emoji)}
      disabled={blocked}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
        blocked
          ? 'border-royal-700 bg-royal-900/20 opacity-50 cursor-not-allowed'
          : 'border-royal-600 bg-royal-900/40 hover:border-gold/50 hover:bg-royal-800/40'
      }`}
    >
      <span className="text-2xl leading-none" aria-hidden="true">
        {emoji.emoji_char}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-medium">{emoji.display_name}</span>
          {emoji.isdefault && (
            <span className="px-1.5 py-0.5 bg-gold/15 text-gold text-xs rounded-full">
              Default
            </span>
          )}
          {isInactive && (
            <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 text-xs rounded-full">
              Disabled
            </span>
          )}
        </div>
        {emoji.description && (
          <p className="text-royal-200 text-xs truncate">{emoji.description}</p>
        )}
      </div>
      <span className="text-gold text-sm flex-shrink-0">
        {blocked ? '—' : '+ Add'}
      </span>
    </button>
  );
}

// ─── Live Preview ─────────────────────────────────────────────────────────
// Renders a small replica of the floating reaction bar so the admin can
// see exactly what the recipient will see. Non-interactive.
function ReactionBarPreview({ emojis, enabled }) {
  if (!enabled) {
    return (
      <div className="text-center py-4">
        <p className="text-royal-200 text-sm italic">No reaction bar will appear on this card.</p>
      </div>
    );
  }

  if (emojis.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-royal-200 text-sm italic">Pick at least one emoji below — otherwise no bar will appear.</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-4">
      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/55 backdrop-blur-md border border-white/20 shadow-lg shadow-black/30">
        {emojis.map((emoji) => (
          <div
            key={emoji.emojiid}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-lg"
          >
            <span className="leading-none" aria-hidden="true">{emoji.emoji_char}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────
export default function TemplateReactionsModal({ template, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Server state mirror
  const [reactionsEnabled, setReactionsEnabled] = useState(true);
  const [assigned, setAssigned] = useState([]);  // [{ emojiid, emoji_char, display_name, ... }]
  const [library, setLibrary] = useState([]);    // full emoji library

  // Drag state for the selected list
  const [draggedIndex, setDraggedIndex] = useState(null);

  // ─── Load ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!template?.templateid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getTemplateReactions(template.templateid);

      setReactionsEnabled(data.reactions_enabled !== false);
      // Server returns assigned in display_order. We trust the server's
      // order — defensive sort just in case.
      const sortedAssigned = (data.assigned || []).slice().sort(
        (a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0)
      );
      setAssigned(sortedAssigned);
      setLibrary(data.library || []);
    } catch (err) {
      setError(err?.data?.error || 'Failed to load reactions config');
    } finally {
      setLoading(false);
    }
  }, [template?.templateid]);

  useEffect(() => { load(); }, [load]);

  // ─── Derived: emojis in library but not yet assigned ─────────────────
  // Hide already-assigned emojis from the picker. Inactive library emojis
  // appear at the bottom (visually deprioritized via opacity in the row).
  const assignedIds = new Set(assigned.map(e => e.emojiid));
  const pickable = library
    .filter(e => !assignedIds.has(e.emojiid))
    .sort((a, b) => {
      // Active first, then by display_order
      const aActive = a.isactive !== false;
      const bActive = b.isactive !== false;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0);
    });

  // ─── Add / Remove ────────────────────────────────────────────────────
  const handleAdd = (emoji) => {
    setAssigned(prev => [...prev, emoji]);
  };

  const handleRemove = (emojiid) => {
    setAssigned(prev => prev.filter(e => e.emojiid !== emojiid));
  };

  // ─── Use Defaults ────────────────────────────────────────────────────
  // Replaces the current selection with whatever the library currently
  // marks as default. Active emojis only.
  const handleUseDefaults = () => {
    const defaults = library
      .filter(e => e.isdefault && e.isactive !== false)
      .sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
    setAssigned(defaults);
  };

  // ─── Drag handlers ───────────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex === null || draggedIndex === index) return;
    setAssigned(prev => {
      const next = prev.slice();
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleDrop = (e) => e.preventDefault();
  const handleDragEnd = () => setDraggedIndex(null);

  // ─── Save ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminService.setTemplateReactions(template.templateid, {
        reactions_enabled: reactionsEnabled,
        emojiids: assigned.map(e => e.emojiid),
      });
      if (onSaved) onSaved();
      else onClose();
    } catch (err) {
      setError(err?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-gold font-display text-lg sm:text-xl">Assign Reactions</h2>
            <p className="text-royal-200 text-sm truncate">
              Template: <span className="text-white">{template?.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Enable toggle */}
              <div className="p-3 border border-royal-600 bg-royal-900/40 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reactionsEnabled}
                    onChange={(e) => setReactionsEnabled(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-gold"
                  />
                  <div>
                    <span className="text-white text-base font-medium">
                      Allow reactions on cards using this template
                    </span>
                    <p className="text-royal-200 text-sm mt-0.5">
                      Turn off to hide the reaction bar entirely on this template's cards. Useful for templates where reactions don't fit the tone (e.g. sympathy or memorial cards).
                    </p>
                  </div>
                </label>
              </div>

              {/* Live preview */}
              <div className="p-4 border border-royal-700/40 bg-royal-800/30 rounded-xl">
                <p className="text-royal-200 text-xs font-semibold uppercase tracking-widest mb-2 text-center">Preview</p>
                <ReactionBarPreview emojis={assigned} enabled={reactionsEnabled} />
              </div>

              {/* Disabled-state veil for the picker */}
              <div className={reactionsEnabled ? '' : 'opacity-60 pointer-events-none'}>
                {/* Selected list */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-sm font-semibold">
                      Selected ({assigned.length})
                    </p>
                    <button
                      onClick={handleUseDefaults}
                      className="text-gold text-sm hover:text-gold-light transition-colors"
                      type="button"
                      disabled={!reactionsEnabled}
                    >
                      Use library defaults
                    </button>
                  </div>

                  {assigned.length === 0 ? (
                    <div className="px-3 py-4 border border-dashed border-royal-600 rounded-lg text-center">
                      <p className="text-royal-200 text-sm">
                        No emojis assigned. Add some from the library below, or click <span className="text-gold">Use library defaults</span>.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {assigned.map((emoji, i) => (
                        <SelectedRow
                          key={emoji.emojiid}
                          emoji={emoji}
                          index={i}
                          isDragging={draggedIndex === i}
                          onDragStart={handleDragStart}
                          onDragOver={handleDragOver}
                          onDragEnd={handleDragEnd}
                          onDrop={handleDrop}
                          onRemove={handleRemove}
                          disabled={!reactionsEnabled}
                        />
                      ))}
                    </div>
                  )}

                  {assigned.length > 0 && (
                    <p className="text-royal-200 text-xs mt-2 italic">
                      Drag rows to reorder. Order shown here = order on the card.
                    </p>
                  )}
                </div>

                {/* Library picker */}
                <div className="mt-5">
                  <p className="text-white text-sm font-semibold mb-2">
                    Library {pickable.length > 0 && <span className="text-royal-200 font-normal">({pickable.length} available)</span>}
                  </p>

                  {pickable.length === 0 ? (
                    <div className="px-3 py-4 border border-royal-700 rounded-lg text-center">
                      <p className="text-royal-200 text-sm">
                        Every library emoji is already assigned to this template.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {pickable.map((emoji) => (
                        <LibraryPickRow
                          key={emoji.emojiid}
                          emoji={emoji}
                          onAdd={handleAdd}
                          disabled={!reactionsEnabled}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t border-royal-700/50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}